import { email, required, url } from 'vuelidate/lib/validators';
import type { FormConfig, IndexableRecord } from './types';
import { publicFieldNames } from '@/components/partials/public-portfolio/constants';
import { linkedInUrl } from '~/components/utils/validate/rules';

const emptyOrLinkedinUrlValidation = (val: string) => !val || linkedInUrl(val);

export const formFieldNames: IndexableRecord = Object.freeze({
  FIRSTNAME: 'firstname',
  LASTNAME: 'lastname',
  WAR_ROOM_YEARS_OF_EXPERIENCE: 'warRoomYearsOfExperience',
  CITY_ID: 'cityId',
  POSITION: 'position',
  COMPANY_ID: 'companyId',
  PHONE: 'phone',
  PHONE_IS_PUBLIC: publicFieldNames.FIELD_PHONE_IS_PUBLIC,
  HAS_OPTED_IN_TO_SMS: 'hasOptedInToSms',
  PUBLIC_EMAIL: 'publicEmail',
  PUBLIC_EMAIL_IS_PUBLIC: publicFieldNames.FIELD_PUBLIC_EMAIL_IS_PUBLIC,
  LINKEDIN_URL: 'linkedinUrl',
  LINKEDIN_SLUG_IS_PUBLIC: publicFieldNames.FIELD_LINKEDIN_SLUG_IS_PUBLIC,
  CALENDAR_LINK: 'calendarLink',
  CALENDAR_LINK_IS_PUBLIC: publicFieldNames.FIELD_CALENDAR_LINK_IS_PUBLIC,
});

export const inputNames: IndexableRecord = Object.freeze({
  BRAVADO_TEXT_INPUT_V3: 'BravadoTextInputV3',
  BRAVADO_CHECKBOX_V3: 'BravadoCheckboxV3',
  PORTFOLIO_PUBLICITY_TOGGLE: 'PortfolioPublicityToggle',
  JOBS_METRO_AREA_INPUT: 'JobsMetroAreaInput',
  JOBS_BACK_OFFICE_COMPANY_AUTOCOMPLETE: 'JobsBackOfficeCompanyAutocomplete',
});

export const formFields: FormConfig = [
  {
    attributeName: formFieldNames.FIRSTNAME,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'First name',
      placeholder: 'First name',
    },
    validation: {
      required,
    },
  },
  {
    attributeName: formFieldNames.LASTNAME,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'Last name',
      placeholder: 'Last name',
    },
    validation: {
      required,
    },
  },
  {
    attributeName: formFieldNames.POSITION,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'Current role',
      placeholder: 'Current role',
    },
    validation: {
      required,
    },
  },
  {
    attributeName: formFieldNames.WAR_ROOM_YEARS_OF_EXPERIENCE,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'Years of sales experience',
      placeholder: 'Years of sales experience',
    },
    validation: {
      required,
    },
    upsertAsInt: true,
  },
  {
    attributeName: formFieldNames.CITY_ID,
    type: inputNames.JOBS_METRO_AREA_INPUT,
    attrs: {
      label: 'What metro area do you live in?',
      placeholder: 'Select metro area',
    },
    validation: {
      required,
    },
    fullWidth: true,
    upsertAsInt: true,
  },
  {
    attributeName: formFieldNames.COMPANY_ID,
    type: inputNames.JOBS_BACK_OFFICE_COMPANY_AUTOCOMPLETE,
    attrs: {
      label: 'Current company',
      placeholder: 'i.e. Acme Corp',
    },
    fullWidth: true,
  },
  {
    attributeName: formFieldNames.PHONE,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'Phone number',
      placeholder: '+149583939548',
    },
    hasPublicityToggle: true,
  },
  {
    attributeName: formFieldNames.PHONE_IS_PUBLIC,
    type: inputNames.PORTFOLIO_PUBLICITY_TOGGLE,
    attrs: {
      size: 'md',
    },
  },
  {
    attributeName: formFieldNames.HAS_OPTED_IN_TO_SMS,
    type: inputNames.BRAVADO_CHECKBOX_V3,
    attrs: {
      label:
        'By entering your phone number and clicking the box, you consent to receive promotional and transactional messages relating to job opportunities, scheduling interviews and more via SMS. Message and data rates may apply. Reply STOP to opt out.',
      size: 'sm',
      align: 'top',
    },
    fullWidth: true,
  },
  {
    attributeName: formFieldNames.PUBLIC_EMAIL,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'Contact email',
      placeholder: 'your-email@gmail.com',
    },
    validation: {
      email,
    },
    hasPublicityToggle: true,
  },
  {
    attributeName: formFieldNames.PUBLIC_EMAIL_IS_PUBLIC,
    type: inputNames.PORTFOLIO_PUBLICITY_TOGGLE,
    attrs: {
      size: 'md',
    },
  },
  {
    attributeName: formFieldNames.LINKEDIN_URL,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'Linkedin Url',
      placeholder: 'http://linkedin.com/in/...',
      valueFormatter: decodeURI,
      valueParser: decodeURI,
    },
    validation: {
      emptyOrLinkedinUrlValidation,
    },
    hasPublicityToggle: true,
  },
  {
    attributeName: formFieldNames.LINKEDIN_SLUG_IS_PUBLIC,
    type: inputNames.PORTFOLIO_PUBLICITY_TOGGLE,
    attrs: {
      size: 'md',
    },
  },
  {
    attributeName: formFieldNames.CALENDAR_LINK,
    type: inputNames.BRAVADO_TEXT_INPUT_V3,
    attrs: {
      label: 'Calendar Url',
      placeholder: 'http://calendly.com/...',
    },
    validation: {
      url,
    },
    hasPublicityToggle: true,
  },
  {
    attributeName: formFieldNames.CALENDAR_LINK_IS_PUBLIC,
    type: inputNames.PORTFOLIO_PUBLICITY_TOGGLE,
    attrs: {
      size: 'md',
    },
  },
];
